/**
 * OpenPersonalChatPage — symmetric entry point for opening the
 * canonical direct personal chat.
 *
 * URL contract: /chat/open/:targetProfileId
 *
 * Bucket C (ACT-INDEEP-FIGMA-DIRECT-CHAT-BACKEND-SLICE): the route
 * name changed from friendProfileId to targetProfileId; both
 * Sponsor-to-Friend and Friend-to-Sponsor resolve to the same
 * canonical channel row.
 *
 * Behaviour:
 *   1. On mount, calls POST /api/v1/chat/channels/personal with
 *      target_profile_id (idempotent; 201 for new, 200 for existing).
 *   2. On success, navigates to /chat/:channelId which the existing
 *      ChatChannelPage consumes.
 *   3. On error, renders the safe ChatError message; the backend
 *      remains authoritative for the actual CHAT_READY decision.
 *
 * Elm-ish: this page is a thin React shell. State transitions live
 * in the chat program (see features/chat/useChatProgram.ts). No
 * business logic or authorization decisions are made in this file.
 */
import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ChatError,
  openPersonalChat as apiOpenPersonalChat,
} from '@/api/chat';
import { RoutePaths, buildChatChannelPath } from '@/routes';

function safeErrorMessage(err: unknown): string {
  if (err instanceof ChatError) {
    switch (err.message) {
      case 'authentication_required':
        return 'Please log in to open a chat.';
      case 'not_authorized':
        // Bucket C: the rejection no longer mentions Intro. Keep
        // the user-facing copy generic; the server is authoritative
        // and returns a non-leaking reason.
        return 'You are not allowed to chat with this person right now.';
      case 'target_profile_not_found':
        return 'This profile could not be found.';
      case 'service_unavailable':
        return 'The chat service is temporarily unavailable. Please try again.';
      default:
        return err.safe ? err.message : 'Could not open chat. Please try again.';
    }
  }
  return 'Could not open chat. Please try again.';
}

export function OpenPersonalChatPage() {
  const params = useParams<{ targetProfileId: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    const targetProfileId = params.targetProfileId;
    if (!targetProfileId) {
      setError('Missing target profile id.');
      return;
    }

    apiOpenPersonalChat(targetProfileId)
      .then(({ channel }) => {
        navigate(buildChatChannelPath(channel.id), { replace: true });
      })
      .catch((err) => {
        setError(safeErrorMessage(err));
      });
  }, [params.targetProfileId, navigate]);

  if (error) {
    return (
      <section className="chat-open-error" data-testid="open-personal-chat-error">
        <h1>Open chat</h1>
        <p>{error}</p>
        <Link to={RoutePaths.CHAT}>Back to chats</Link>
      </section>
    );
  }

  return (
    <section className="chat-open-loading" data-testid="open-personal-chat-loading">
      <p>Opening chat…</p>
    </section>
  );
}
